/** Inter cap height as a fraction of the em (1490 / 2048 font units). */
export const INTER_CAP_HEIGHT_EM = 0.727;

export type LabelLayout = {
  /** x for every line (`textAnchor="middle"` anchor). */
  x: number;
  /** Alphabetic-baseline y for each line, top to bottom. */
  ys: number[];
  lineGap: number;
};

/**
 * Lay out an ALL-CAPS multi-line label so its visible ink is centred on
 * (cx, cy), using the default alphabetic baseline.
 *
 * SVG `<text y>` is the baseline, not the middle of the glyphs. Caps
 * occupy [baseline - capH, baseline], so the line's optical centre sits
 * capH / 2 above its baseline. The block's optical centre is the middle
 * of (first cap-top .. last baseline), so:
 *
 *   baseline[i] = cy + (i - (n - 1) / 2) * lineGap + capH / 2
 *
 * which makes mean(baseline) - capH / 2 === cy. (The previous formula
 * used `cy - n * lineGap / 2 + 0.35 * fontSize`, which puts the mean
 * baseline at cy - lineGap / 2 + 0.35 * fontSize, i.e. the block sat
 * about 0.5 * fontSize + 1 px above centre.)
 *
 * `letterSpacing` is applied after every glyph including the last, so a
 * middle-anchored line is shifted left by letterSpacing / 2; nudge x
 * right by the same amount.
 */
export function labelLayout(
  cx: number,
  cy: number,
  lineCount: number,
  fontSize: number,
  letterSpacing = 0,
  lineGap = fontSize + 2,
): LabelLayout {
  const capH = INTER_CAP_HEIGHT_EM * fontSize;
  const ys = Array.from(
    { length: lineCount },
    (_, i) => cy + (i - (lineCount - 1) / 2) * lineGap + capH / 2,
  );
  return { x: cx + letterSpacing / 2, ys, lineGap };
}
